import { Model } from './model';
import type { AgentMetrics, AgentParsingErrorCode } from './types';
import { Type } from 'class-transformer';
import { Attachment } from './attachment';

export enum KnowledgeEntryType {
  ACH_INFORMATION = 'ach_information',
  REMIT_ADDRESS = 'remit_address',
  W9_FORM = 'w9_form',
}
export enum ExtractionStatus {
  PENDING = 'pending',
  NEEDS_REVIEW = 'needs_review',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface ProfileAddress {
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
  suite?: string | null;
  country?: string;
}

export interface W9Form {
  name: string;
  tax_classification?: string | null;
  address?: ProfileAddress;
  ein?: string | null;
  business_name?: string | null;
  exempt_payee_code?: string | null;
  exemption_from_fatca_code?: string | null;
  list_account_numbers?: string | null;
  requesters_name_address?: string | null;
  tin?: string | null;
}

export interface ACHInformation {
  bank_name: string;
  account_name: string;
  account_number: string;
  routing_number: string;
  ach_filters?: string | null;
  wire_routing_number?: string | null;
  swift_code?: string | null;
  account_address?: ProfileAddress | null;
}

export class KnowledgeEntry extends Model {
  id: string;

  agent_confidence_reason: string | null;

  agent_confidence_score: number | null;

  agent_error_code: AgentParsingErrorCode | null;

  agent_uncertainties: string[];

  entry_type: KnowledgeEntryType;

  // Business unit this entry is scoped to. Null = organization-wide default;
  // a business-unit-scoped entry overrides the default for partners in that unit.
  business_unit_id: string | null;

  extraction_status: ExtractionStatus;

  extracted_content: W9Form | ACHInformation | Record<string, any>;

  @Type(() => Attachment)
  attachment: Attachment | null;

  get agentMetrics(): AgentMetrics {
    return {
      agentConfidenceReason: this.agent_confidence_reason,
      agentConfidenceScore: this.agent_confidence_score,
      agentErrorCode: this.agent_error_code,
      agentUncertainties: this.agent_uncertainties,
    };
  }

  get agentConfidenceLevel(): 'high' | 'medium' | 'low' | null {
    return this.agent_confidence_score === null
      ? null
      : this.agent_confidence_score >= 95
        ? 'high'
        : this.agent_confidence_score >= 85
          ? 'medium'
          : 'low';
  }
}
